import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import type { GetStaticPaths, GetStaticProps } from 'next';
import * as Sentry from '@sentry/nextjs';
import Button from 'epn-ui/umd/dist/components/Button';
import List from 'epn-ui/umd/dist/components/List';

// Components
import Grid from 'epn-ui/umd/dist/components/Grid';
import ListDialogItem from '@components/pages/support/ListDialogItem';
import DialogsStatuses from '@components/pages/support/DialogsStatuses';
import SupportHead from '@components/pages/support/SupportHead';
import EmptyDialogs from '@components/pages/support/EmptyDialogs';

// Helpers
import useI18n, { LocaleTypes } from '@i18n';
import apiHooks, { ICreateUseRequestInfiniteResponse, ICreateUseRequestResponse } from '@apiHooks';
import {
    IGetUserProfileDataFormatted,
    ISupportDialogCountByStatus,
    ISupportDialogsDataAttributes,
} from '@api/types';

// Config
import { ALL_STATUS, CLOSE_STATUS, OPEN_STATUS } from '@components/pages/support/config';
import routes from '@config/routes.json';

// Interfaces
import { IPreparedMessage } from '@components/pages/support/CreateMessageModal/templates/interface';

const { useDialogs, useUserProfile, useSupportMessageCount } = apiHooks;

const Support: React.FC = () => {
    const [askQuestionsIsOpen, setAckQuestionIsOpen] = useState<boolean>(false);
    const [chooseThemeIsOpen, setChooseThemeIsOpen] = useState<boolean>(false);
    const [createMessageIsOpen, setCreateMessageIsOpen] = useState<boolean>(false);
    const [preparedMessage, setPreparedMessage] = useState<IPreparedMessage>(null);

    const router = useRouter();
    const { status, theme, createSupportMessage } = router.query;
    const { t, locale } = useI18n('Support');
    const { t: tCommon } = useI18n('Common');

    useEffect(() => {
        if (Number(createSupportMessage)) {
            setCreateMessageIsOpen(true);
            if (typeof theme === 'string' && theme.length > 0) {
                try {
                    setPreparedMessage({ subject: decodeURI(theme) });
                } catch (err) {
                    Sentry.captureException(err);
                }
            }
        }
    }, [createSupportMessage, theme]);

    // Профиль пользователя
    const { data: userData } = useUserProfile<IGetUserProfileDataFormatted>();
    const { profileImage } = userData || {};

    // Список диалогов пользователя, с саппортом
    const { data: dialogs, isLoading: dialogsIsLoading, isReachingEnd, nextPage } = useDialogs({
        params: {
            offset: 0,
            limit: 10,
            ticket_status: status,
        },
        condition: !!status,
        dependencies: [locale, router],
    }) as ICreateUseRequestInfiniteResponse<ISupportDialogsDataAttributes[]>;

    // Кол-во диалогов пользователя вместе со статусами
    const { data: unreadedMessagesCount = {}, mutate: updateMessageCount } = useSupportMessageCount(
        {
            condition: !!status,
            dependencies: [locale],
        },
    ) as ICreateUseRequestResponse<ISupportDialogCountByStatus>;

    /**
     * @param {string} newStatus
     * @returns {Promise<boolean>}
     */
    const changeStatusListRoute = (newStatus: string): Promise<boolean> => {
        return router.push(`/${locale}${routes.support.list.replace(':status', newStatus)}`);
    };

    /**
     * @param {string} newStatus
     * @returns {void}
     */
    const onStatusActionHandler = (newStatus: string): void => {
        changeStatusListRoute(newStatus);
    };

    if (typeof status === 'string' && ![ALL_STATUS, OPEN_STATUS, CLOSE_STATUS].includes(status)) {
        changeStatusListRoute('all');
        return null;
    }

    return (
        <div className="mb-[100px]">
            <div className="w-full">
                <SupportHead
                    askQuestionsIsOpen={askQuestionsIsOpen}
                    onAskQuestionChange={state => setAckQuestionIsOpen(state)}
                    chooseThemeIsOpen={chooseThemeIsOpen}
                    onChooseThemeChange={state => setChooseThemeIsOpen(state)}
                    createMessageIsOpen={createMessageIsOpen}
                    onCreateMessageChange={state => setCreateMessageIsOpen(state)}
                    updateMessageCount={updateMessageCount}
                    preparedMessage={preparedMessage}
                />
            </div>
            <Grid gutter={[24, 24]}>
                <Grid.Col span={24} xs={24} md={6} xl={4}>
                    <DialogsStatuses
                        countByStatus={unreadedMessagesCount}
                        currentStatus={Array.isArray(status) ? status[0] : status || ''}
                        onAction={onStatusActionHandler}
                    />

                    <div className="block mb-6 xs:hidden">
                        <Button
                            className="w-full"
                            type="primary"
                            onClick={() => setAckQuestionIsOpen(true)}
                        >
                            {t('Ask question')}
                        </Button>
                    </div>
                </Grid.Col>

                <Grid.Col span={24} xs={24} md={18} xl={20}>
                    <List
                        separator
                        bordered={Array.isArray(dialogs) && dialogs.length > 0}
                        dataSource={dialogs}
                        className="border-blueGray-200"
                        loading={dialogsIsLoading}
                        renderItem={(item: ISupportDialogsDataAttributes) => (
                            <ListDialogItem item={item} profileImage={profileImage || ''} />
                        )}
                        customEmpty={
                            <EmptyDialogs onAskQuesiton={state => setAckQuestionIsOpen(state)} />
                        }
                    />
                    {!isReachingEnd &&
                        !dialogsIsLoading &&
                        Array.isArray(dialogs) &&
                        dialogs.length > 0 && (
                            <div className="flex flex-row justify-center mt-[16px] sm:mt-[24px] lg:mt-[32px]">
                                <Button
                                    type="primary"
                                    onClick={() => nextPage()}
                                    className="w-full xs:w-auto"
                                >
                                    {tCommon('Load more')}
                                </Button>
                            </div>
                        )}
                </Grid.Col>
            </Grid>
        </div>
    );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    return { props: { locale: params.locale, status: params.status } };
};

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [
            { params: { locale: LocaleTypes.ru, status: OPEN_STATUS } },
            { params: { locale: LocaleTypes.ru, status: CLOSE_STATUS } },
            { params: { locale: LocaleTypes.ru, status: ALL_STATUS } },
            { params: { locale: LocaleTypes.en, status: OPEN_STATUS } },
            { params: { locale: LocaleTypes.en, status: CLOSE_STATUS } },
            { params: { locale: LocaleTypes.en, status: ALL_STATUS } },
        ],
        fallback: false,
    };
};

export default Support;
